import React from 'react';

import { AddressCard } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/AddressCard';
import { AddressDialog } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/AddressDialog';
import { AddressPreview } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/AddressPreview';
import { BackToAutocompleteButton } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/buttons/BackToAutocompleteButton';
import { CancelChangesButton } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/buttons/CancelChangesButton';
import { ConfirmChangesButton } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/buttons/ConfirmChangesButton';
import {
  Field,
  FieldProps,
} from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/Field';
import { GoogleMapsAutocomplete } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/GoogleMapsAutocomplete';
import { NonControlledField } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/NonControlledField';
import {
  AddressContextProvider,
  useAddressContext,
} from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/contexts/AddressContext';
import { useAddressFormFields } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/hooks/useAddressFormFields';
import { AddressMode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/types/AddressMode';

interface AutocompleteAddressHeadlessProps {
  children: React.ReactNode;
  fieldNamesPrefix?: string;
}

export const AutocompleteAddressHeadless = ({
  children,
  fieldNamesPrefix = 'address',
}: AutocompleteAddressHeadlessProps) => {
  return <AddressContextProvider fieldNamesPrefix={fieldNamesPrefix}>{children}</AddressContextProvider>;
};

AutocompleteAddressHeadless.Address = ({ apiKey, ...props }: { apiKey: string } & Omit<FieldProps, 'name' | 'as'>) => {
  return (
    <GoogleMapsAutocomplete
      subFieldName={'address'}
      label={'ADDRESS'}
      placeholder={'Gran Vía, 23, San Sebastián, 20002, España'}
      apiKey={apiKey}
      {...props}
    />
  );
};

AutocompleteAddressHeadless.AddressPreview = (props) => {
  return (
    <AddressPreview
      subFieldName={'address'}
      label={'ADDRESS'}
      placeholder={'Gran Vía, 23, San Sebastián, 20002, España'}
      {...props}
    />
  );
};

AutocompleteAddressHeadless.AddressCard = AddressCard;

AutocompleteAddressHeadless.StreetName = ({ as, ...props }: Omit<FieldProps, 'name'>) => {
  const { focusStreetName } = useAddressContext();

  return (
    <Field
      subFieldName="streetName"
      as={as}
      label={'STREET_NAME'}
      placeholder="Carrer de Muntaner"
      {...props}
      inputProps={{ maxLength: 50, ...props.inputProps }}
      inputRef={(input) => (focusStreetName ? input && input.focus() : input)}
    />
  );
};

AutocompleteAddressHeadless.StreetNumber = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field subFieldName="streetNumber" as={as} label={'STREET_NUMBER'} placeholder="293" {...props} />
);

AutocompleteAddressHeadless.FloorNumber = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field
    subFieldName="floorNumber"
    as={as}
    label={'FLOOR_NUMBER'}
    {...props}
    inputProps={{ maxLength: 2, ...props.inputProps }}
  />
);

AutocompleteAddressHeadless.DoorNumber = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field
    subFieldName="doorNumber"
    as={as}
    label={'DOOR_NUMBER'}
    {...props}
    inputProps={{ maxLength: 1, ...props.inputProps }}
  />
);

AutocompleteAddressHeadless.Building = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field
    subFieldName="building"
    as={as}
    label={'BUILDING_LABEL'}
    {...props}
    inputProps={{ maxLength: 5, ...props.inputProps }}
  />
);

AutocompleteAddressHeadless.PostalCode = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field subFieldName="postalCode" as={as} label={'POSTAL_CODE'} placeholder="08001" {...props} />
);

AutocompleteAddressHeadless.City = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field
    subFieldName="city"
    as={as}
    label={'CITY'}
    placeholder="Barcelona"
    {...props}
    inputProps={{ maxLength: 30, ...props.inputProps }}
  />
);

AutocompleteAddressHeadless.Province = ({ as, ...props }: Omit<FieldProps, 'name'>) => (
  <Field subFieldName="province" as={as} label={'PROVINCE'} placeholder="Catalunya" {...props} />
);

AutocompleteAddressHeadless.Country = ({ as, ...props }: Omit<FieldProps, 'name' | 'value'>) => {
  const { updateCountryLongName } = useAddressFormFields();
  return (
    <NonControlledField
      subFieldName="country"
      as={as}
      label={'COUNTRY'}
      placeholder="España"
      autocompleteProps={{
        onChange: (_, value) => {
          updateCountryLongName(value);
        },
      }}
      {...props}
    />
  );
};

AutocompleteAddressHeadless.Dialog = AddressDialog;

AutocompleteAddressHeadless.CancelChangesButton = () => {
  const { mode } = useAddressContext();
  const { hasTemporaryCardData } = useAddressFormFields();

  if (mode === AddressMode.AUTOCOMPLETE && !hasTemporaryCardData) return null;

  if (!hasTemporaryCardData) {
    return <BackToAutocompleteButton />;
  } else {
    return <CancelChangesButton />;
  }
};

AutocompleteAddressHeadless.ConfirmChangesButton = () => {
  const { cardHasChanges, areFieldsValid } = useAddressFormFields();

  return <ConfirmChangesButton disabled={!cardHasChanges || !areFieldsValid} />;
};
